import { memo, useCallback, useState, useRef } from "react";
import { useProdutos } from "./useProdutos";
import "./Produtos.scss";
import { produtosService } from "./produtosService";
import Swal from "sweetalert2";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr.replace(" ", "T"));
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatusBadge = memo(({ ativo }) => (
  <span className={`badge ${ativo ? "badge--ativo" : "badge--inativo"}`}>
    {ativo ? "Ativo" : "Inativo"}
  </span>
));
StatusBadge.displayName = "StatusBadge";

const TipoTabs = memo(({ value, onChange, counts }) => (
  <div className="tipo-tabs" role="tablist" aria-label="Filtrar por tipo">
    {[
      { key: "produto", label: "Produtos", icon: "📦" },
      { key: "servico", label: "Serviços", icon: "🧰" },
      { key: "alugavel", label: "Alugáveis", icon: "🏷️" },
    ].map(({ key, label, icon }) => (
      <button
        key={key}
        role="tab"
        aria-selected={value === key}
        className={`tipo-tab ${value === key ? "tipo-tab--active" : ""}`}
        onClick={() => onChange(key)}
        type="button"
      >
        <span className="tipo-tab__icon" aria-hidden="true">
          {icon}
        </span>
        {label}
        <span className="tipo-tab__count">{counts[key] ?? 0}</span>
      </button>
    ))}
  </div>
));
TipoTabs.displayName = "TipoTabs";

const SearchBar = memo(({ value, onChange }) => (
  <div className="search-bar">
    <span className="search-bar__icon" aria-hidden="true">
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.35-4.35" />
      </svg>
    </span>
    <input
      type="search"
      placeholder="Buscar por nome, descrição ou EAN..."
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="search-bar__input"
      aria-label="Buscar produtos"
    />
    {value && (
      <button
        className="search-bar__clear"
        onClick={() => onChange("")}
        aria-label="Limpar busca"
        type="button"
      >
        ×
      </button>
    )}
  </div>
));
SearchBar.displayName = "SearchBar";

// ─── Linha da tabela ──────────────────────────────────────────────────────────

const ProdutoRow = memo(({ produto, onEdit, onToggle, abaAtiva }) => {
  const isAlugavel = abaAtiva === "alugavel";
  const isProduto = abaAtiva === "produto";

  return (
    <tr className={!produto.ativo ? "row--inativo" : ""}>
      <td data-label="Criado em">{formatDate(produto.criado_em)}</td>

      {(abaAtiva === "produto" || abaAtiva === "alugavel") && (
        <td data-label="Imagem">
          {produto.imagem_url ? (
            <img
              src={produto.imagem_url}
              alt={produto.nome}
              style={{
                width: 40,
                height: 40,
                objectFit: "cover",
                borderRadius: 6,
              }}
            />
          ) : (
            <span className="text-muted">—</span>
          )}
        </td>
      )}

      <td data-label="Nome" className="cell--nome">
        {produto.nome}
      </td>
      <td data-label="Nome">{produto.marca}</td>
      {isProduto && (
        <td data-label="EAN">
          <code className="ean-code">{produto.ean || "—"}</code>
        </td>
      )}

      <td data-label="Descrição" className="cell--descricao">
        {produto.descricao || <span className="text-muted">Sem descrição</span>}
      </td>

      {isProduto && (
        <td data-label="Estoque" className="cell--estoque">
          <span
            className={`estoque-badge ${produto.estoque <= 0 ? "estoque-badge--zero" : ""}`}
          >
            {produto.estoque ?? 0}
          </span>
        </td>
      )}

      {isAlugavel && (
        <td data-label="Valor/dia">
          <span className="preco-badge">
            {produto.valor_diaria ? (
              Number(produto.valor_diaria).toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
              })
            ) : (
              <span className="text-muted">—</span>
            )}
          </span>
        </td>
      )}

      <td data-label="Status">
        <StatusBadge ativo={produto.ativo} />
      </td>
      <td data-label="Ações">
        <div className="actions">
          <button
            className="btn btn--ghost btn--sm"
            onClick={() => onEdit(produto)}
            aria-label={`Editar ${produto.nome}`}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            Editar
          </button>
          <button
            className={`btn btn--sm ${produto.ativo ? "btn--danger-ghost" : "btn--success-ghost"}`}
            onClick={() => onToggle(produto)}
            aria-label={
              produto.ativo
                ? `Desativar ${produto.nome}`
                : `Ativar ${produto.nome}`
            }
          >
            {produto.ativo ? (
              <>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="8" y1="12" x2="16" y2="12" />
                </svg>
                Desativar
              </>
            ) : (
              <>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="16" />
                  <line x1="8" y1="12" x2="16" y2="12" />
                </svg>
                Ativar
              </>
            )}
          </button>
        </div>
      </td>
    </tr>
  );
});
ProdutoRow.displayName = "ProdutoRow";

// ─── Empty / Loading ──────────────────────────────────────────────────────────

const TIPO_LABEL = {
  produto: "produto",
  servico: "serviço",
  alugavel: "item alugável",
};

const EmptyState = memo(({ hasFilter, tipo }) => (
  <tr>
    <td colSpan="8">
      <div className="empty-state">
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
        >
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        </svg>
        <p>
          {hasFilter
            ? `Nenhum ${TIPO_LABEL[tipo] ?? "item"} encontrado para esta busca.`
            : `Nenhum ${TIPO_LABEL[tipo] ?? "item"} cadastrado ainda.`}
        </p>
        {hasFilter && <span>Tente outros termos ou limpe o filtro.</span>}
      </div>
    </td>
  </tr>
));
EmptyState.displayName = "EmptyState";

const LoadingState = memo(() => (
  <tr>
    <td colSpan="8">
      <div className="loading-state">
        <div className="spinner" aria-label="Carregando..." role="status" />
        <span>Carregando...</span>
      </div>
    </td>
  </tr>
));
LoadingState.displayName = "LoadingState";

// ─── Modal ────────────────────────────────────────────────────────────────────

const ProdutoModal = memo(({ isOpen, form, onChange, onSubmit, onClose }) => {
  const isEditing = Boolean(form.id);
  const isServico = form.tipo === "servico";
  const isAlugavel = form.tipo === "alugavel";
  const isProduto = !isServico && !isAlugavel;

  const handleChange = useCallback(
    (field) => (e) => {
      const value =
        e.target.type === "checkbox" ? e.target.checked : e.target.value;
      onChange({ ...form, [field]: value });
    },
    [form, onChange],
  );

  const handleTipoChange = useCallback(
    (novoTipo) => {
      onChange({
        ...form,
        tipo: novoTipo,
        // limpa campos que não pertencem ao novo tipo
        ...(novoTipo === "servico"
          ? { ean: "", estoque: 0, valor_diaria: null }
          : {}),
        ...(novoTipo === "alugavel" ? { ean: "", preco_custo: 0 } : {}),
        ...(novoTipo === "produto" ? { valor_diaria: null } : {}),
      });
    },
    [form, onChange],
  );

  // Label dinâmico do título
  const tipoLabel = isServico ? "Serviço" : isAlugavel ? "Alugável" : "Produto";

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      onKeyDown={(e) => e.key === "Escape" && onClose()}
    >
      {/* ── modal com scroll interno e footer sticky ── */}
      <div className="modal modal--scrollable">
        {/* HEADER — fixo no topo */}
        <div className="modal__header">
          <h3 id="modal-title" className="modal__title">
            {isEditing ? `Editar ${tipoLabel}` : `Novo ${tipoLabel}`}
          </h3>
          <button
            className="modal__close"
            onClick={onClose}
            aria-label="Fechar modal"
            type="button"
          >
            ×
          </button>
        </div>

        {/* BODY — rola */}
        <form
          id="produto-form"
          className="modal__body"
          onSubmit={onSubmit}
          noValidate
        >
          {/* ── Seletor de tipo ── */}
          <div className="form-group">
            <label className="form-label">Tipo</label>
            <div
              className="tipo-selector tipo-selector--three"
              role="group"
              aria-label="Tipo de cadastro"
            >
              <button
                type="button"
                className={`tipo-option ${isProduto ? "tipo-option--active" : ""}`}
                onClick={() => handleTipoChange("produto")}
                disabled={isEditing}
              >
                <span className="tipo-option__icon">📦</span>
                <span className="tipo-option__label">Produto</span>
                <span className="tipo-option__desc">Controla estoque</span>
              </button>
              <button
                type="button"
                className={`tipo-option ${isServico ? "tipo-option--active" : ""}`}
                onClick={() => handleTipoChange("servico")}
                disabled={isEditing}
              >
                <span className="tipo-option__icon">🔧</span>
                <span className="tipo-option__label">Serviço</span>
                <span className="tipo-option__desc">Não move estoque</span>
              </button>
              <button
                type="button"
                className={`tipo-option ${isAlugavel ? "tipo-option--active" : ""}`}
                onClick={() => handleTipoChange("alugavel")}
                disabled={isEditing}
              >
                <span className="tipo-option__icon">🏷️</span>
                <span className="tipo-option__label">Alugável</span>
                <span className="tipo-option__desc">Valor por dia</span>
              </button>
            </div>
            {isEditing && (
              <span className="form-hint">
                O tipo não pode ser alterado após o cadastro.
              </span>
            )}
          </div>

          {/* ── Nome ── */}
          <div className="form-group">
            <label htmlFor="nome" className="form-label">
              Nome{" "}
              <span className="required" aria-hidden="true">
                *
              </span>
            </label>
            <input
              id="nome"
              className="form-input"
              placeholder={
                isServico
                  ? "Ex: Troca de tela, Alinhamento..."
                  : isAlugavel
                    ? "Ex: Salão de Festa, Kit Cadeiras..."
                    : "Ex: Parafuso M8, Camiseta Branca..."
              }
              value={form.nome}
              onChange={handleChange("nome")}
              required
              autoFocus
              maxLength={120}
            />
          </div>

          {/* ── EAN — somente produto ── */}
          {isProduto && (
            <div className="form-group">
              <label htmlFor="ean" className="form-label">
                EAN / Código de Barras
              </label>
              <input
                id="ean"
                className="form-input"
                placeholder="Ex: 7891234567890"
                value={form.ean || ""}
                onChange={handleChange("ean")}
                inputMode="numeric"
                maxLength={13}
              />
            </div>
          )}

          {/* ── Marca ── */}
          <div className="form-group">
            <label htmlFor="marca" className="form-label">
              Marca
            </label>
            <input
              id="marca"
              className="form-input"
              placeholder="Ex: Nike, Samsung, 3M..."
              value={form.marca || ""}
              onChange={handleChange("marca")}
              maxLength={80}
            />
          </div>

          {/* ── Imagem ── */}
          <div className="form-group">
            <label className="form-label">Imagem</label>

            {!form.id ? (
              <p className="form-hint">
                💡 Salve o cadastro primeiro para adicionar uma imagem.
              </p>
            ) : (
              <ImageUploader
                produtoId={form.id}
                imagemUrl={form.imagem_url}
                onUpload={(url) => onChange({ ...form, imagem_url: url })}
                onRemove={() => onChange({ ...form, imagem_url: null })}
              />
            )}
          </div>

          {/* ── Campos de preço ── */}
          <div className="form-row form-row--two">
            {/* Preço de custo — produto e serviço */}
            {!isAlugavel && (
              <div className="form-group">
                <label htmlFor="preco_custo" className="form-label">
                  {isServico ? "Preço do Serviço" : "Preço de Custo"}
                  <span className="required" aria-hidden="true">
                    {" "}
                    *
                  </span>
                </label>
                <div className="input-prefix">
                  <span className="input-prefix__symbol">R$</span>
                  <input
                    id="preco_custo"
                    className="form-input form-input--prefixed"
                    placeholder="0,00"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.preco_custo || ""}
                    onChange={handleChange("preco_custo")}
                    required
                  />
                </div>
              </div>
            )}

            {/* Valor por dia — somente alugável */}
            {isAlugavel && (
              <div className="form-group">
                <label htmlFor="valor_diaria" className="form-label">
                  Valor por dia <span className="required">*</span>
                </label>
                <div className="input-prefix">
                  <span className="input-prefix__symbol">R$</span>
                  <input
                    id="valor_diaria"
                    className="form-input form-input--prefixed"
                    placeholder="0,00"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.valor_diaria || ""}
                    onChange={handleChange("valor_diaria")}
                    required
                  />
                </div>
              </div>
            )}

            {/* Estoque inicial — produto, apenas na criação */}
            {isProduto && !isEditing && (
              <div className="form-group">
                <label htmlFor="estoque" className="form-label">
                  Estoque inicial
                </label>
                <input
                  id="estoque"
                  className="form-input"
                  placeholder="0"
                  type="number"
                  min="0"
                  step="1"
                  value={form.estoque || ""}
                  onChange={handleChange("estoque")}
                />
              </div>
            )}

            {/* Quantidade disponível — alugável, apenas na criação */}
            {isAlugavel && !isEditing && (
              <div className="form-group">
                <label htmlFor="estoque" className="form-label">
                  Quantidade disponível
                </label>
                <input
                  id="estoque"
                  className="form-input"
                  placeholder="Ex: 5"
                  type="number"
                  min="0"
                  step="1"
                  value={form.estoque || ""}
                  onChange={handleChange("estoque")}
                />
              </div>
            )}
          </div>

          {/* ── Descrição ── */}
          <div className="form-group">
            <label htmlFor="descricao" className="form-label">
              Descrição
            </label>
            <textarea
              id="descricao"
              className="form-input form-textarea"
              placeholder={
                isServico
                  ? "Descreva o serviço prestado..."
                  : isAlugavel
                    ? "Descreva o item, condições, observações..."
                    : "Descreva o produto..."
              }
              value={form.descricao || ""}
              onChange={handleChange("descricao")}
              rows={3}
              maxLength={500}
            />
            <span className="char-count">
              {form.descricao?.length ?? 0}/500
            </span>
          </div>
        </form>

        {/* FOOTER — fixo no rodapé do modal */}
        <div className="modal__footer">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="submit"
            form="produto-form"
            className="btn btn--primary"
          >
            {isEditing
              ? `Salvar ${tipoLabel.toLowerCase()}`
              : `Criar ${tipoLabel.toLowerCase()}`}
          </button>
        </div>
      </div>
    </div>
  );
});
ProdutoModal.displayName = "ProdutoModal";

const ImageUploader = ({ produtoId, imagemUrl, onUpload, onRemove }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const data = await produtosService.uploadImagem(produtoId, file);
      onUpload(data.imagem_url);
    } catch {
      Swal.fire("Erro", "Não foi possível enviar a imagem.", "error");
    } finally {
      setUploading(false);
      // limpa o input para permitir re-upload do mesmo arquivo
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleRemove = async () => {
    const confirm = await Swal.fire({
      title: "Remover imagem?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#DC2626",
      cancelButtonColor: "#64748B",
    });
    if (!confirm.isConfirmed) return;

    try {
      await produtosService.removerImagem(produtoId);
      onRemove();
    } catch {
      Swal.fire("Erro", "Não foi possível remover a imagem.", "error");
    }
  };

  return (
    <div className="image-uploader">
      {imagemUrl ? (
        <div className="image-uploader__preview">
          <img
            src={imagemUrl}
            alt="Imagem do produto"
            className="image-uploader__img"
          />
          <div className="image-uploader__actions">
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? "Enviando..." : "Trocar"}
            </button>
            <button
              type="button"
              className="btn btn--danger-ghost btn--sm"
              onClick={handleRemove}
              disabled={uploading}
            >
              Remover
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="image-uploader__dropzone"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? (
            <span>Enviando...</span>
          ) : (
            <>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                aria-hidden="true"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <span>Clique para enviar</span>
              <span className="form-hint">JPG, PNG ou WebP · máx. 5 MB</span>
            </>
          )}
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: "none" }}
        onChange={handleFile}
      />
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const Produtos = () => {
  const {
    produtos,
    loading,
    modalOpen,
    form,
    filtro,
    setFiltro,
    setForm,
    openModal,
    closeModal,
    handleSubmit,
    handleToggleAtivo,
  } = useProdutos();

  const [abaAtiva, setAbaAtiva] = useState("produto");

  const counts = {
    produto: produtos.filter((p) => p.tipo === "produto" || !p.tipo).length,
    servico: produtos.filter((p) => p.tipo === "servico").length,
    alugavel: produtos.filter((p) => p.tipo === "alugavel").length,
  };

  const produtosFiltrados = produtos.filter((p) => {
    const tipoCorreto =
      abaAtiva === "produto"
        ? p.tipo === "produto" || !p.tipo
        : abaAtiva === "servico"
          ? p.tipo === "servico"
          : /* alugavel */ p.tipo === "alugavel";

    if (!tipoCorreto) return false;
    if (!filtro) return true;

    const termo = filtro.toLowerCase();
    return (
      p.nome?.toLowerCase().includes(termo) ||
      p.descricao?.toLowerCase().includes(termo) ||
      p.ean?.toLowerCase().includes(termo)
    );
  });

  const handleOpenModal = useCallback(
    (produto = null) => {
      if (produto) {
        openModal(produto);
      } else {
        openModal({ tipo: abaAtiva });
      }
    },
    [openModal, abaAtiva],
  );

  const LABEL = {
    produto: { singular: "produto", plural: "produtos", novo: "Novo Produto" },
    servico: { singular: "serviço", plural: "serviços", novo: "Novo Serviço" },
    alugavel: {
      singular: "item alugável",
      plural: "itens alugáveis",
      novo: "Novo Alugável",
    },
  };

  const label = LABEL[abaAtiva] ?? LABEL.produto;

  return (
    <div className="page-container">
      {/* ── Header ── */}
      <div className="page-header">
        <div className="page-header__info">
          <h1 className="page-title">Produtos & Serviços</h1>
          <p className="page-subtitle">
            {produtosFiltrados.length}{" "}
            {produtosFiltrados.length !== 1 ? label.plural : label.singular}{" "}
            encontrado
            {produtosFiltrados.length !== 1 ? "s" : ""}
          </p>
        </div>
        <button className="btn btn--primary" onClick={() => handleOpenModal()}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            aria-hidden="true"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {label.novo}
        </button>
      </div>

      {/* ── Abas ── */}
      <TipoTabs value={abaAtiva} onChange={setAbaAtiva} counts={counts} />

      {/* ── Busca ── */}
      <div className="page-toolbar">
        <SearchBar value={filtro} onChange={setFiltro} />
      </div>

      {/* ── Tabela ── */}
      <div className="table-wrapper">
        <table
          className="data-table"
          role="grid"
          aria-label={`Lista de ${label.plural}`}
        >
          <thead>
            <tr>
              <th scope="col">Criado em</th>
              {(abaAtiva === "produto" || abaAtiva === "alugavel") && (
                <th scope="col">Imagem</th>
              )}
              <th scope="col">Nome</th>
              <th scope="col">Marca</th>
              {abaAtiva === "produto" && <th scope="col">EAN</th>}
              <th scope="col">Descrição</th>
              {abaAtiva === "produto" && <th scope="col">Estoque</th>}
              {abaAtiva === "alugavel" && <th scope="col">Valor/dia</th>}
              <th scope="col">Status</th>
              <th scope="col">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <LoadingState />
            ) : produtosFiltrados.length === 0 ? (
              <EmptyState hasFilter={Boolean(filtro)} tipo={abaAtiva} />
            ) : (
              produtosFiltrados.map((p) => (
                <ProdutoRow
                  key={p.id}
                  produto={p}
                  onEdit={handleOpenModal}
                  onToggle={handleToggleAtivo}
                  abaAtiva={abaAtiva}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Modal ── */}
      <ProdutoModal
        isOpen={modalOpen}
        form={form}
        onChange={setForm}
        onSubmit={handleSubmit}
        onClose={closeModal}
      />
    </div>
  );
};

export default Produtos;
